import React, { useState } from "react";

const AGENTS = [
  { icon: "❤️", name: "Symptom Agent", desc: "Analyzing symptoms & conditions", color: "#EF4444", bg: "#FEE2E2" },
  { icon: "📋", name: "History Agent", desc: "Checking medical history", color: "#8B5CF6", bg: "#EDE9FE" },
  { icon: "👨‍⚕️", name: "Doctor Matcher", desc: "Finding best specialist", color: "#10B981", bg: "#D1FAE5" },
  { icon: "📍", name: "Location Agent", desc: "Locating nearby doctors", color: "#3B82F6", bg: "#DBEAFE" },
  { icon: "🏥", name: "Care Planner", desc: "Creating your care plan", color: "#F59E0B", bg: "#FEF3C7" },
];

function Sidebar({ activePage, setActivePage }) {
  const items = [
    { icon: "🏠", page: "home", label: "Home" },
    { icon: "🏥", page: "agents", label: "Agents" },
    { icon: "❤️", page: "health", label: "Health" },
    { icon: "📄", page: "records", label: "Records" },
    { icon: "⚙️", page: "settings", label: "Settings" },
  ];
  return (
    <div style={{
      width: "64px",
      background: "#FFFFFF",
      borderRight: "1px solid #E8EDF5",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "20px 0",
      gap: "8px",
      height: "100vh",
      position: "fixed",
      left: 0, top: 0,
      zIndex: 100,
      boxShadow: "2px 0 12px rgba(0,0,0,0.04)",
    }}>
      {items.map((item, i) => (
        <div key={i}
          onClick={() => setActivePage(item.page)}
          title={item.label}
          style={{
            width: "44px", height: "44px",
            borderRadius: "12px",
            background: activePage === item.page
              ? "linear-gradient(135deg, #3B82F6, #8B5CF6)"
              : "transparent",
            display: "flex", alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            cursor: "pointer",
            marginTop: i === 0 ? "8px" : "0",
            transition: "all 0.2s",
            border: activePage === item.page
              ? "none" : "1px solid transparent",
          }}>
          {item.icon}
        </div>
      ))}
    </div>
  );
}

function AgentCard({ agent, active, done }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      padding: "14px 16px",
      borderRadius: "14px",
      backgroundColor: "#FFFFFF",
      marginBottom: "10px",
      border: active
        ? `2px solid ${agent.color}`
        : "2px solid transparent",
      boxShadow: active
        ? `0 4px 20px ${agent.color}25`
        : "0 2px 8px rgba(0,0,0,0.04)",
      transition: "all 0.3s",
    }}>
      <div style={{
        width: "42px", height: "42px",
        borderRadius: "12px",
        background: agent.bg,
        display: "flex", alignItems: "center",
        justifyContent: "center",
        fontSize: "20px",
        marginRight: "14px",
        flexShrink: 0,
      }}>{agent.icon}</div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontWeight: "700",
          color: "#1E293B",
          fontSize: "0.92rem",
        }}>{agent.name}</div>
        <div style={{
          color: "#94A3B8",
          fontSize: "0.78rem",
          marginTop: "2px",
        }}>{agent.desc}</div>
      </div>
      {(active || done) && (
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
        }}>
          <div style={{
            width: "8px", height: "8px",
            borderRadius: "50%",
            backgroundColor: done ? "#10B981" : agent.color,
          }} />
          <span style={{
            fontSize: "0.75rem",
            color: done ? "#10B981" : agent.color,
            fontWeight: "600",
          }}>{done ? "Done ✓" : "Analyzing..."}</span>
        </div>
      )}
    </div>
  );
}

function ResultSection({ icon, title, color, bg, children }) {
  return (
    <div style={{
      backgroundColor: "#FFFFFF",
      borderRadius: "16px",
      padding: "24px",
      marginBottom: "16px",
      border: `1.5px solid ${bg}`,
      borderLeft: `5px solid ${color}`,
    }}>
      <div style={{
        display: "flex", alignItems: "center",
        gap: "10px", marginBottom: "16px",
      }}>
        <div style={{
          width: "38px", height: "38px",
          borderRadius: "10px", background: bg,
          display: "flex", alignItems: "center",
          justifyContent: "center", fontSize: "18px",
        }}>{icon}</div>
        <h3 style={{
          margin: 0, color: color,
          fontSize: "1rem", fontWeight: "800",
        }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}

function InputField({ label, name, placeholder,
  icon, value, onChange }) {
  return (
    <div style={{ marginBottom: "14px" }}>
      <label style={{
        display: "block",
        fontSize: "0.72rem",
        fontWeight: "700",
        color: "#94A3B8",
        marginBottom: "6px",
        textTransform: "uppercase",
        letterSpacing: "0.8px",
      }}>{icon} {label}</label>
      <input
        style={{
          width: "100%",
          padding: "11px 14px",
          borderRadius: "10px",
          border: "1.5px solid #E8EDF5",
          fontSize: "0.92rem",
          color: "#1E293B",
          backgroundColor: "#F8FAFF",
          outline: "none",
          boxSizing: "border-box",
          transition: "border 0.2s",
          fontFamily: "inherit",
        }}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={(e) =>
          e.target.style.borderColor = "#3B82F6"}
        onBlur={(e) =>
          e.target.style.borderColor = "#E8EDF5"}
      />
    </div>
  );
}

export default function App() {
  const [form, setForm] = useState({
    symptoms: "",
    age: "",
    gender: "Male",
    location: "",
    patientName: "",
    bloodGroup: "",
    existingConditions: "",
    currentMedications: "",
    allergies: "",
    previousSurgeries: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(-1);
  const [doneSteps, setDoneSteps] = useState([]);
  const [activePage, setActivePage] = useState("home");

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const scrollToForm = () => {
    document.getElementById("patient-form")
      .scrollIntoView({ behavior: "smooth" });
  };

  const handleSubmit = async () => {
    setLoading(true);
    setResult(null);
    setDoneSteps([]);

    for (let i = 0; i < 5; i++) {
      setActiveStep(i);
      await new Promise((r) => setTimeout(r, 700));
      setDoneSteps((prev) => [...prev, i]);
    }

    try {
      const res = await fetch(
  "https://mediagent-mjp5.onrender.com/api/mediagent/analyze",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...form,
            existingConditions: form.existingConditions
              .split(",").map((s) => s.trim()),
            currentMedications: form.currentMedications
              .split(",").map((s) => s.trim()),
            allergies: form.allergies
              .split(",").map((s) => s.trim()),
          }),
        }
      );
      const data = await res.json();
      setResult(data);
    } catch (err) {
      alert("Error: " + err.message);
    }

    setLoading(false);
    setActiveStep(-1);
  };

  return (
    <div style={{
      minHeight: "100vh",
      backgroundColor: "#F0F4FF",
      fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>

      <Sidebar
        activePage={activePage}
        setActivePage={setActivePage}
      />

      <div style={{ marginLeft: "64px", minHeight: "100vh" }}>

        {/* Navbar */}
        <div style={{
          background: "#FFFFFF",
          padding: "0 32px",
          height: "64px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #E8EDF5",
          position: "sticky", top: 0, zIndex: 50,
        }}>
          <div style={{
            display: "flex",
            alignItems: "center", gap: "12px",
          }}>
            <div style={{
              width: "38px", height: "38px",
              borderRadius: "10px",
              background: "linear-gradient(135deg, #3B82F6, #8B5CF6)",
              display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "18px",
            }}>🏥</div>
            <div>
              <div style={{
                fontWeight: "800", fontSize: "1.1rem",
                color: "#1E293B",
              }}>MediAgent</div>
              <div style={{
                fontSize: "0.72rem", color: "#94A3B8",
              }}>AI-Powered Patient Care</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            {["🔔", "👤"].map((ic, i) => (
              <div key={i} style={{
                width: "38px", height: "38px",
                borderRadius: "10px",
                background: "#F0F4FF",
                display: "flex", alignItems: "center",
                justifyContent: "center",
                fontSize: "18px", cursor: "pointer",
              }}>{ic}</div>
            ))}
          </div>
        </div>

        {/* Hero Grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          minHeight: "calc(100vh - 64px)",
        }}>

          {/* LEFT */}
          <div style={{
            padding: "32px",
            borderRight: "1px solid #E8EDF5",
            background: "#FFFFFF",
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "28px",
            }}>
              <div style={{
                width: "48px", height: "48px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #3B82F6, #8B5CF6)",
                display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: "24px",
              }}>💊</div>
              <div>
                <h2 style={{
                  margin: 0, fontSize: "1.6rem",
                  fontWeight: "800", color: "#1E293B",
                }}>5 Health Agents</h2>
                <p style={{
                  margin: 0, color: "#94A3B8",
                  fontSize: "0.85rem",
                }}>Analyzing your health 📈</p>
              </div>
            </div>

            {/* Agent Cards */}
            {AGENTS.map((agent, i) => (
              <AgentCard
                key={i}
                agent={agent}
                active={activeStep === i}
                done={doneSteps.includes(i)}
              />
            ))}

            {/* Scroll Indicator */}
            <div
              onClick={scrollToForm}
              style={{
                textAlign: "center",
                padding: "12px",
                color: "#3B82F6",
                fontSize: "0.88rem",
                fontWeight: "700",
                cursor: "pointer",
                marginTop: "8px",
                marginBottom: "8px",
                animation: "bounce 2s ease-in-out infinite",
                background: "#EFF6FF",
                borderRadius: "10px",
                border: "1.5px dashed #3B82F6",
              }}>
              ↓ Scroll down to fill patient details
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              style={{
                width: "100%",
                padding: "16px",
                background: loading
                  ? "#94A3B8"
                  : "linear-gradient(135deg, #3B82F6, #8B5CF6)",
                color: "white",
                border: "none",
                borderRadius: "14px",
                fontSize: "1rem",
                fontWeight: "800",
                cursor: loading ? "not-allowed" : "pointer",
                boxShadow: loading
                  ? "none"
                  : "0 8px 24px rgba(59,130,246,0.35)",
                transition: "all 0.3s",
              }}>
              {loading
                ? `⚡ Agent ${activeStep + 1}/5 Working...`
                : "🔍 Start AI Analysis"}
            </button>
          </div>

          {/* RIGHT — Teddy Bear */}
          <div style={{
            background: "linear-gradient(160deg, #EFF6FF 0%, #F5F0FF 100%)",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px",
            overflow: "hidden",
          }}>
            {/* BG Circles */}
            <div style={{
              position: "absolute",
              top: "-80px", right: "-80px",
              width: "350px", height: "350px",
              borderRadius: "50%",
              background: "rgba(59,130,246,0.08)",
            }} />
            <div style={{
              position: "absolute",
              bottom: "-60px", left: "-60px",
              width: "280px", height: "280px",
              borderRadius: "50%",
              background: "rgba(139,92,246,0.08)",
            }} />
            <div style={{
              position: "absolute",
              top: "40%", right: "-20px",
              width: "180px", height: "180px",
              borderRadius: "50%",
              background: "rgba(239,68,68,0.06)",
            }} />

            {/* Stats */}
            <div style={{
              position: "absolute",
              top: "24px", left: "24px",
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "14px 20px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              zIndex: 2,
            }}>
              <div style={{
                fontSize: "2.2rem", fontWeight: "900",
                color: "#8B5CF6", lineHeight: 1,
              }}>5</div>
              <div style={{
                fontSize: "0.72rem", color: "#94A3B8",
                fontWeight: "600", marginTop: "4px",
              }}>AI Agents</div>
            </div>

            <div style={{
              position: "absolute",
              top: "24px", right: "24px",
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "14px 20px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              zIndex: 2,
            }}>
              <div style={{
                fontSize: "2.2rem", fontWeight: "900",
                color: "#10B981", lineHeight: 1,
              }}>3</div>
              <div style={{
                fontSize: "0.72rem", color: "#94A3B8",
                fontWeight: "600", marginTop: "4px",
              }}>Top Doctors</div>
            </div>

            <div style={{
              position: "absolute",
              bottom: "140px", right: "20px",
              background: "#FFFFFF",
              borderRadius: "16px",
              padding: "14px 20px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              zIndex: 2,
            }}>
              <div style={{
                fontSize: "1.6rem", fontWeight: "900",
                color: "#EF4444", lineHeight: 1,
              }}>24/7</div>
              <div style={{
                fontSize: "0.72rem", color: "#94A3B8",
                fontWeight: "600", marginTop: "4px",
              }}>Available</div>
            </div>

            {/* TEDDY BEAR — BIG */}
            <div style={{
              animation: "float 3s ease-in-out infinite",
              zIndex: 1,
              marginTop: "-20px",
            }}>
              <img
                src="/doctor-bear.png"
                alt="Doctor Bear"
                style={{
                  width: "520px",
                  maxHeight: "620px",
                  objectFit: "contain",
                  filter: "drop-shadow(0 24px 48px rgba(59,130,246,0.25))",
                }}
              />
            </div>
          </div>
        </div>

        {/* PATIENT FORM */}
        <div id="patient-form" style={{
          maxWidth: "860px",
          margin: "0 auto",
          padding: "40px 32px",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "28px",
          }}>
            <div style={{
              width: "44px", height: "44px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #EF4444, #F59E0B)",
              display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "22px",
            }}>📝</div>
            <div>
              <h2 style={{
                margin: 0, fontSize: "1.5rem",
                fontWeight: "800", color: "#1E293B",
              }}>Patient Details</h2>
              <p style={{
                margin: 0, color: "#94A3B8",
                fontSize: "0.82rem",
              }}>Fill in your information below</p>
            </div>
          </div>

          <div style={{
            background: "#FFFFFF",
            borderRadius: "20px",
            padding: "32px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
            border: "1.5px solid #E8EDF5",
          }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0 24px",
            }}>
              <InputField label="Patient Name"
                name="patientName"
                placeholder="e.g.Rahul Sharma" icon="👤"
                value={form.patientName}
                onChange={handleChange} />
              <InputField label="Age" name="age"
                placeholder="e.g.45" icon="🎂"
                value={form.age}
                onChange={handleChange} />
              <InputField label="Location" name="location"
                placeholder="e.g.Noida" icon="📍"
                value={form.location}
                onChange={handleChange} />
              <InputField label="Blood Group"
                name="bloodGroup"
                placeholder="e.g.B+" icon="🩸"
                value={form.bloodGroup}
                onChange={handleChange} />
              <InputField label="Existing Conditions / current health issues"
                name="existingConditions"
                placeholder="e.g.diabetes, hypertension" icon="🏥"
                value={form.existingConditions}
                onChange={handleChange} />
              <InputField label="Current Medications(daily medicines)"
                name="currentMedications"
                placeholder="e.g.metformin, Peracetamol" icon="💊"
                value={form.currentMedications}
                onChange={handleChange} />
            </div>

            <InputField label="Allergies" name="allergies"
              placeholder="e.g.Itching, pollen" icon="⚠️"
              value={form.allergies}
              onChange={handleChange} />

            {/* Gender */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{
                display: "block", fontSize: "0.72rem",
                fontWeight: "700", color: "#94A3B8",
                marginBottom: "8px",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}>⚧ Gender</label>
              <div style={{ display: "flex", gap: "10px" }}>
                {["Male", "Female", "Other"].map((g) => (
                  <button key={g}
                    onClick={() =>
                      setForm({ ...form, gender: g })}
                    style={{
                      padding: "10px 24px",
                      borderRadius: "10px",
                      border: `2px solid ${form.gender === g
                        ? "#3B82F6" : "#E8EDF5"}`,
                      background: form.gender === g
                        ? "#EFF6FF" : "white",
                      color: form.gender === g
                        ? "#3B82F6" : "#94A3B8",
                      fontWeight: "700",
                      cursor: "pointer",
                      fontSize: "0.9rem",
                      transition: "all 0.2s",
                    }}>{g}</button>
                ))}
              </div>
            </div>

            {/* Symptoms */}
            <div>
              <label style={{
                display: "block", fontSize: "0.72rem",
                fontWeight: "700", color: "#94A3B8",
                marginBottom: "6px",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
              }}>🩺 Symptoms</label>
              <textarea
                style={{
                  width: "100%", padding: "12px 14px",
                  borderRadius: "10px",
                  border: "1.5px solid #E8EDF5",
                  fontSize: "0.92rem", color: "#1E293B",
                  backgroundColor: "#F8FAFF",
                  height: "100px", resize: "vertical",
                  boxSizing: "border-box",
                  fontFamily: "inherit", outline: "none",
                  transition: "border 0.2s",
                }}
                name="symptoms"
                placeholder="Describe your symptoms in detail... e.g. chest pain, shortness of breath"
                value={form.symptoms}
                onChange={handleChange}
                onFocus={(e) =>
                  e.target.style.borderColor = "#3B82F6"}
                onBlur={(e) =>
                  e.target.style.borderColor = "#E8EDF5"}
              />
            </div>
          </div>
        </div>

        {/* RESULTS */}
        {result && (
          <div style={{
            maxWidth: "860px",
            margin: "0 auto",
            padding: "0 32px 60px",
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "24px",
            }}>
              <div style={{
                width: "44px", height: "44px",
                borderRadius: "12px",
                background: "linear-gradient(135deg, #10B981, #3B82F6)",
                display: "flex", alignItems: "center",
                justifyContent: "center", fontSize: "22px",
              }}>🎯</div>
              <h2 style={{
                margin: 0, fontSize: "1.5rem",
                fontWeight: "800", color: "#1E293B",
              }}>AI Analysis Results</h2>
            </div>

            <ResultSection icon="❤️"
              title="Agent 1 — Symptom Analysis"
              color="#EF4444" bg="#FEE2E2">
              <div style={{ marginBottom: "12px" }}>
                {[
                  {
                    text: `⚡ ${result.symptomAnalysis.severity} Severity`,
                    color: "#EF4444", bg: "#FEE2E2",
                  },
                  {
                    text: `👨‍⚕️ ${result.symptomAnalysis.recommendedSpecialist}`,
                    color: "#8B5CF6", bg: "#EDE9FE",
                  },
                ].map((b, i) => (
                  <span key={i} style={{
                    display: "inline-block",
                    padding: "5px 14px",
                    borderRadius: "20px",
                    background: b.bg, color: b.color,
                    fontWeight: "700", fontSize: "0.82rem",
                    marginRight: "8px",
                  }}>{b.text}</span>
                ))}
              </div>
              <p style={{
                color: "#475569", lineHeight: "1.7",
                whiteSpace: "pre-line", margin: 0,
              }}>{result.symptomAnalysis.analysis}</p>
            </ResultSection>

            <ResultSection icon="📋"
              title="Agent 2 — Medical History"
              color="#8B5CF6" bg="#EDE9FE">
              <p style={{
                color: "#475569", lineHeight: "1.7",
                whiteSpace: "pre-line", margin: 0,
              }}>{result.historyAnalysis}</p>
            </ResultSection>

            <ResultSection icon="👨‍⚕️"
              title="Agent 3 — Doctor Match"
              color="#10B981" bg="#D1FAE5">
              <p style={{
                color: "#475569", lineHeight: "1.7",
                whiteSpace: "pre-line", margin: 0,
              }}>{result.doctorMatch}</p>
            </ResultSection>

            <ResultSection icon="📍"
              title="Agent 4 — Nearby Doctors"
              color="#3B82F6" bg="#DBEAFE">
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(3,1fr)",
                gap: "16px",
              }}>
                {result.nearbyDoctors.map((doc, i) => (
                  <div key={i} style={{
                    background: "#F8FAFF",
                    borderRadius: "14px",
                    padding: "20px 16px",
                    border: "1.5px solid #DBEAFE",
                    textAlign: "center",
                  }}>
                    <div style={{
                      fontSize: "40px",
                      marginBottom: "8px",
                    }}>👨‍⚕️</div>
                    <h4 style={{
                      margin: "0 0 4px",
                      color: "#3B82F6",
                      fontSize: "0.88rem",
                      fontWeight: "800",
                    }}>{doc.name}</h4>
                    <p style={{
                      margin: "2px 0",
                      fontSize: "0.78rem",
                      color: "#94A3B8",
                    }}>🏥 {doc.hospital}</p>
                    <p style={{
                      margin: "2px 0",
                      fontSize: "0.78rem",
                      color: "#94A3B8",
                    }}>📍 {doc.address}</p>
                    <div style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "4px",
                      justifyContent: "center",
                      marginTop: "10px",
                    }}>
                      {[
                        { t: `⭐ ${doc.rating}`, c: "#F59E0B", b: "#FEF3C7" },
                        { t: doc.fees, c: "#10B981", b: "#D1FAE5" },
                        { t: doc.distance, c: "#3B82F6", b: "#DBEAFE" },
                      ].map((badge, j) => (
                        <span key={j} style={{
                          padding: "3px 10px",
                          borderRadius: "20px",
                          background: badge.b,
                          color: badge.c,
                          fontSize: "0.75rem",
                          fontWeight: "700",
                        }}>{badge.t}</span>
                      ))}
                    </div>
                    <div style={{
                      marginTop: "10px",
                      padding: "5px 12px",
                      background: "#D1FAE5",
                      borderRadius: "20px",
                      color: "#10B981",
                      fontSize: "0.75rem",
                      fontWeight: "700",
                    }}>{doc.availability}</div>
                  </div>
                ))}
              </div>
            </ResultSection>

            <ResultSection icon="🏥"
              title="Agent 5 — Care Plan"
              color="#F59E0B" bg="#FEF3C7">
              <p style={{
                color: "#475569", lineHeight: "1.7",
                whiteSpace: "pre-line", margin: 0,
              }}>{result.carePlan}</p>
            </ResultSection>
          </div>
        )}
      </div>
    </div>
  );
}